import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Ear, Brain, FileSearch, ClipboardList, Repeat, HeartPulse } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { IMAGES } from "../lib/clinic";

const STEPS = [
  { icon: Ear, title: "Listen", desc: "We start by hearing your story — not rushing through symptoms." },
  { icon: FileSearch, title: "Assess", desc: "Detailed history, focused examination and review of any reports." },
  { icon: Brain, title: "Diagnose", desc: "A clear explanation in simple language — what's happening and why." },
  { icon: ClipboardList, title: "Plan", desc: "A personalized, practical treatment plan that fits your life." },
  { icon: HeartPulse, title: "Treat", desc: "Modern protocols, careful prescribing, and family counseling when needed." },
  { icon: Repeat, title: "Follow Up", desc: "Regular reviews to adjust the plan and track real progress." },
];

const PRINCIPLES = [
  { title: "Patient first", desc: "Every plan starts with you — your goals, your worries, your lifestyle." },
  { title: "Whole-person care", desc: "Brain and mind treated together — body, mood, sleep, stress." },
  { title: "Evidence-based", desc: "Modern protocols, careful prescribing, no unnecessary tests." },
  { title: "Transparent", desc: "Clear explanations, fair pricing, no surprises." },
  { title: "Confidential", desc: "Your privacy is sacred — always." },
  { title: "Accessible", desc: "Online and in-clinic care, with WhatsApp follow-up." },
];

const PatientCare = () => {
  return (
    <div data-testid="patient-care-page">
      <PageHeader
        eyebrow="Patient Care · Treatment Approach"
        title="Care that listens before it prescribes."
        subtitle="A calm, structured, six-step approach that has helped thousands of patients in neurology and psychology — built around you."
        breadcrumbs={["Patient Care"]}
      />

      <section className="section-y bg-paper">
        <div className="container-x">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">Our 6-step approach</div>
            <h2 className="heading-serif text-4xl md:text-5xl text-brand-navyDark leading-[1.06] font-medium">
              From your first call to long-term wellness.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-paper p-9 md:p-10 hover:bg-paper-soft transition-colors"
                data-testid={`step-${i}`}
              >
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="heading-serif text-2xl text-brand-goldDark font-medium leading-none">
                    0{i + 1}
                  </span>
                  <s.icon className="w-5 h-5 text-brand-tealDark" strokeWidth={1.3} />
                </div>
                <h3 className="heading-serif text-[26px] font-medium text-brand-navyDark mb-3">
                  {s.title}
                </h3>
                <p className="text-[14.5px] text-brand-body leading-[1.8]">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y bg-paper-soft">
        <div className="container-x grid lg:grid-cols-12 gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.doctor_desk} alt="Patient-focused care" className="w-full h-full object-cover grayscale-[8%]" />
            </div>
          </motion.div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-8">
            <div className="eyebrow mb-5">Our Principles</div>
            <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-9">
              The values behind every consultation.
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {PRINCIPLES.map((p) => (
                <div key={p.title} className="py-5 border-b border-stone-200">
                  <div className="heading-serif text-xl text-brand-navyDark font-medium mb-2">{p.title}</div>
                  <p className="text-[14px] text-brand-body leading-[1.75]">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-y bg-paper">
        <div className="container-x max-w-2xl">
          <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-6">
            Care that puts you first.
          </h2>
          <p className="text-brand-body mb-10 text-[16px] leading-[1.8]">
            Whether it's a one-off concern or a long-term condition — let's talk.
          </p>
          <Link to="/contact#book" className="btn-primary" data-testid="patient-care-cta">
            Book Your Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PatientCare;
